import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAdminApi } from './adminApi';
import { SEARCH_GROUPS, SearchResultRow, describeResult } from './searchResults';

const MIN_QUERY = 2;
const PER_GROUP = 3;

function isTyping(element) {
  return element && (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName) || element.isContentEditable);
}

function AdminSearch({ condo }) {
  const api = useAdminApi();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const query = text.trim();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !isTyping(document.activeElement)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);

  useEffect(() => {
    if (query.length < MIN_QUERY) {
      setData(null);
      return undefined;
    }
    let stale = false;
    const timer = setTimeout(() => {
      api.get(`/api/admin/condos/${condo._id}/search`, { q: query, limit: PER_GROUP })
        .then((response) => { if (!stale) { setData(response.data); setActive(-1); } })
        .catch(() => { if (!stale) setData(null); });
    }, 250);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [api, condo._id, query]);

  const rows = useMemo(() => SEARCH_GROUPS.flatMap((group) => (data?.results?.[group.type] || []).map((item) => ({ group, item }))), [data]);

  const go = (to) => {
    setOpen(false);
    setText('');
    setData(null);
    inputRef.current?.blur();
    navigate(to);
  };
  const seeAll = () => go(`/admin/${condo.slug}/search?q=${encodeURIComponent(query)}`);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(-1, i - 1));
    } else if (e.key === 'Enter' && query.length >= MIN_QUERY) {
      e.preventDefault();
      if (active >= 0 && rows[active]) go(describeResult(rows[active].group.type, rows[active].item, condo.slug).to);
      else seeAll();
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showPanel = open && query.length >= MIN_QUERY && data;
  let index = -1;

  return (
    <div ref={boxRef} className="relative w-full lg:max-w-2xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-label="Search this condo"
        aria-expanded={!!showPanel}
        aria-controls="admin-search-results"
        aria-activedescendant={active >= 0 ? `admin-search-option-${active}` : undefined}
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search tickets, residents, units, bulletins, events, forum..."
        className="w-full pl-9 pr-10 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
      />
      <kbd className="hidden md:block absolute right-3 top-1/2 -translate-y-1/2 px-2 rounded border border-border text-xs text-muted-foreground">/</kbd>
      {showPanel && (
        <div id="admin-search-results" role="listbox" aria-label="Search results" className="absolute left-0 right-0 mt-2 z-50 max-h-96 overflow-y-auto bg-card border border-border rounded-lg shadow-lg p-2">
          {rows.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No results for “{query}”.</p>
          ) : (
            SEARCH_GROUPS.filter((group) => data.results[group.type]?.length).map((group) => (
              <div key={group.type} role="group" aria-label={group.label} className="mb-1">
                <p className="px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.label}{data.counts[group.type] > data.results[group.type].length && ` · ${data.counts[group.type]}`}
                </p>
                {data.results[group.type].map((item) => {
                  index += 1;
                  const i = index;
                  return <SearchResultRow key={item._id} id={`admin-search-option-${i}`} group={group} item={item} slug={condo.slug} selected={active === i} onSelect={go} />;
                })}
              </div>
            ))
          )}
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={seeAll} className="block w-full px-3 py-2 mt-1 text-left text-sm text-primary hover:underline border-t border-border">
            See all {data.totalResults} result{data.totalResults === 1 ? '' : 's'} →
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminSearch;
